<script setup lang="ts">
  import { type TableDefaultColumnOptions, VButton, VPopover, VSkeleton } from "@krainovsd/vue-ui";
  import dayjs from "dayjs";
  import { computed, ref } from "vue";
  import { useI18n } from "vue-i18n";
  import { useRoute, useRouter } from "vue-router";
  import { CACHE_NAMESPACE, CACHE_TTL, useCachedResource } from "@/entities/cache";
  import {
    INDICATORS_BY_KEY,
    formatIndicatorValue,
    responseRowsToReportRows,
  } from "@/entities/report";
  import type { ReportResponseRows, ReportRow } from "@/entities/report";
  import { fetchSummaryById } from "@/entities/statistics";
  import type { ISummaryDetail } from "@/entities/statistics";
  import { PAGES } from "@/entities/tech";
  import Ellipsis from "@/components/icons/Ellipsis.vue";
  import IndicatorCard from "@/components/molecules/IndicatorCard.vue";
  import TableSkeleton from "@/components/molecules/TableSkeleton.vue";
  import { Table } from "@/components/organisms/Table";
  import type { ITableColumn } from "@/components/organisms/Table/table.types";
  import { downloadCsv } from "@/lib/csv";
  import type { CsvValue } from "@/lib/csv";

  const { t } = useI18n();
  const route = useRoute();
  const router = useRouter();

  const briefId = Number(route.params.id);

  const cacheKey = `brief-${briefId}`;

  const { data: briefData, isLoading: isBriefLoading } = useCachedResource<ISummaryDetail>({
    namespace: CACHE_NAMESPACE.SummaryList,
    key: cacheKey,
    ttl: CACHE_TTL.Default,
    strategy: "stale-while-revalidate",
    fetcher: () => fetchSummaryById(briefId),
  });

  const reportInfo = computed(() => briefData.value?.summary.reportInfo ?? null);

  const title = computed(() => {
    const dateTo = briefData.value?.dateTo;
    if (!dateTo) return "";
    return t(
      "page.briefs.itemTitle",
      { date: dayjs(dateTo).format("DD.MM.YYYY") },
      { default: "Сводка за {date}" },
    );
  });

  const compareDates = computed(() => {
    const from = briefData.value?.dateFrom;
    const to = briefData.value?.dateTo;
    if (!from || !to) return "";
    return `${dayjs(from).format("DD.MM.YYYY")} — ${dayjs(to).format("DD.MM.YYYY")}`;
  });

  const currencySymbol = computed(() => reportInfo.value?.currency);

  // Indicators from the summary's reportInfo, displayed as cards.
  const indicatorCards = computed(() =>
    (reportInfo.value?.indicators ?? []).flatMap((key) => {
      const indicator = INDICATORS_BY_KEY[key];
      if (!indicator) return [];
      return [
        {
          name: t(indicator.title.path, {}, { default: indicator.title.default }),
          field: indicator.name as keyof ReportRow,
          format: "format" in indicator ? indicator.format : undefined,
        },
      ];
    }),
  );

  // Aggregate indicator totals from all grouping rows (no comparison — only
  // one period in a summary).
  const indicatorTotals = computed<Record<string, number>>(() => {
    const rows = responseRowsToReportRows(
      (briefData.value?.summary.statistics ?? []) as ReportResponseRows[],
    );
    const totals: Record<string, number> = {};
    for (const row of rows) {
      for (const { field } of indicatorCards.value) {
        const value = row[field];
        if (typeof value === "number") {
          totals[field] = (totals[field] ?? 0) + value;
        }
      }
    }
    return totals;
  });

  // --- Table ---
  const DEFAULT_COLUMN: TableDefaultColumnOptions = {
    draggable: false,
    sortable: true,
    filterable: false,
    resizable: false,
    cellClass: ["common", "hCenter"],
    headerClass: ["common"],
  };

  const rows = computed<ReportRow[]>(() =>
    responseRowsToReportRows((briefData.value?.summary.statistics ?? []) as ReportResponseRows[]),
  );

  const groupingColumn = computed<ITableColumn<ReportRow>>(() => ({
    key: "date",
    name: t("page.report.columns.date", {}, { default: "Дата" }),
    minWidth: 220,
    maxWidth: 220,
    sortable: false,
    leftFrozen: true,
    cellRender: {
      component: "expandable",
      props: {
        withDeepGroup: false,
        childNameField: "name",
        childIsDate: false,
        isDate: true,
        formatDate: "DD.MM.YYYY",
        childFormatDate: "DD.MM.YYYY",
        withId: false,
        multiline: false,
      },
    },
    headerRender: { component: "fixable" },
  }));

  const indicatorColumns = computed<ITableColumn<ReportRow>[]>(() =>
    indicatorCards.value.map(({ name, field, format }) => ({
      key: field,
      name,
      minWidth: 110,
      maxWidth: 200,
      cellRender: {
        component: "default",
        props: {
          customContent: (context: { row: { original: ReportRow } }) =>
            formatIndicatorValue(context.row.original[field], format, currencySymbol.value),
        },
      },
    })),
  );

  const columns = computed<ITableColumn<ReportRow>[]>(() => [
    groupingColumn.value,
    ...indicatorColumns.value,
  ]);

  const rowHeight = 120;

  const isMenuOpen = ref(false);

  // --- CSV export ---------------------------------------------------------
  function csvCell(key: string, value: unknown): CsvValue {
    if (key === "date" && typeof value === "string") {
      const parsed = dayjs(value);
      if (parsed.isValid()) return parsed.format("DD.MM.YYYY");
    }
    return typeof value === "number" ? value : "";
  }

  const exportFileName = computed(() => {
    const dateTo = briefData.value?.dateTo;
    const date = dateTo ? dayjs(dateTo).format("DD.MM.YYYY") : "brief";
    return `brief_${date}`;
  });

  function handleExportCsv() {
    isMenuOpen.value = false;
    if (rows.value.length === 0) return;
    const cols = columns.value;
    const header = cols.map((column) => column.name);
    const body = rows.value.map((row) =>
      cols.map((column) => csvCell(String(column.key), row[String(column.key) as keyof ReportRow])),
    );
    downloadCsv(exportFileName.value, [header, ...body]);
  }

  function handleEditTemplate() {
    isMenuOpen.value = false;
    const templateId = briefData.value?.templateId;
    if (templateId !== null) {
      void router.push({
        name: PAGES.EditSubscription,
        params: { id: String(templateId) },
      });
    }
  }
</script>

<template>
  <div :class="$style.base">
    <section :class="[$style.header, $style.pagePadding]">
      <div :class="$style.title">
        <h1 :class="$style.titleText">{{ title }}</h1>
      </div>
      <section :class="$style.actions">
        <VPopover v-model="isMenuOpen" :class-name-positioner-content="$style.menu">
          <VButton><Ellipsis /></VButton>
          <template #content>
            <button type="button" :class="$style.menuItem" @click="handleExportCsv">
              Выгрузка CSV
            </button>
            <button type="button" :class="$style.menuItem" @click="handleEditTemplate">
              Редактировать шаблон
            </button>
          </template>
        </VPopover>
      </section>
    </section>

    <section :class="$style.pagePadding">
      <div :class="$style.compareHeader">
        <span :class="$style.compareDates">{{ compareDates }}</span>
      </div>
      <div :class="$style.compareCards">
        <template v-if="isBriefLoading">
          <div
            v-for="n in indicatorCards.length || 6"
            :key="`skeleton-${n}`"
            :class="$style.skeletonCard"
          >
            <VSkeleton type="text" :animation="true" :class="$style.skeletonName" />
            <VSkeleton type="text" :animation="true" :class="$style.skeletonValue" />
          </div>
        </template>
        <IndicatorCard
          v-for="indicator in indicatorCards"
          v-else
          :key="indicator.field"
          :name="indicator.name"
          :value="
            formatIndicatorValue(
              indicatorTotals[indicator.field] ?? 0,
              indicator.format,
              currencySymbol,
            )
          "
        />
      </div>
    </section>

    <section :class="[$style.table, $style.pagePadding]">
      <TableSkeleton
        v-if="isBriefLoading"
        :columns="columns.map((c) => c.minWidth ?? 120)"
        :header-height="40"
        :row-height="rowHeight"
      />
      <div v-else-if="!rows.length" :class="$style.empty">
        Нет данных с выбранной конфигурацией за период {{ compareDates }}
      </div>
      <Table
        v-else
        :rows="rows"
        :columns="columns"
        :default-column-options="DEFAULT_COLUMN"
        :get-row-id="(row: ReportRow, index: number) => String(index)"
        :virtual-rows="true"
        :virtual-row-size="rowHeight"
        :header-height="40"
        :row-height="rowHeight"
        :with-total="false"
      />
    </section>
  </div>
</template>

<style lang="scss" module>
  .base {
    width: 100%;
    height: 100%;
    max-height: 100%;
    padding-top: 20px;

    overflow: auto;

    display: flex;
    gap: 24px;
    flex-direction: column;
  }

  .pagePadding {
    padding-left: 20px;
    padding-right: 20px;
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 5px;
  }

  .title {
    flex: 1;
    min-width: 0;

    display: flex;
    align-items: center;
    gap: 8px;
  }

  .titleText {
    margin: 0;
    font-weight: 600;
    font-size: 1.5rem;
    line-height: 2rem;

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .actions {
    display: flex;
    gap: 8px;
  }

  .menu {
    min-width: 200px;
    padding: 4px;

    display: flex;
    flex-direction: column;

    background-color: var(--popover);
  }

  .menuItem {
    padding: 8px 12px;

    background: transparent;
    border: none;
    color: var(--foreground);
    cursor: pointer;
    text-align: left;

    font-family: inherit;
    font-size: 0.875rem;
    line-height: 1.25rem;

    &:hover {
      background-color: var(--accent);
    }
  }

  .compareHeader {
    display: flex;
    justify-content: center;
    margin-bottom: 12px;
  }

  .compareDates {
    font-weight: 400;
    color: var(--muted-foreground);
  }

  .compareCards {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
  }

  .skeletonCard {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px 0;
  }

  .skeletonName {
    width: 40%;
    height: 11px;
  }

  .skeletonValue {
    width: 70%;
    height: 1.75rem;
  }

  .table {
    max-height: 600px;
    margin-bottom: 80px;
  }

  .empty {
    padding: 1.5rem;
    border: 1px dashed var(--border);

    color: var(--muted-foreground);
    font-size: 0.875rem;
    line-height: 1.25rem;
  }
</style>
